/**
 * 공휴일 관리 API — /api/admin/holidays* (BFF catch-all proxy → chat-server SSOT).
 * design-20260603-holiday-admin-import — 1차: 번들 전용, 수동 추가·단건 삭제 포함.
 */
import { apiClient } from './client';

export interface HolidayRow {
  id: string;
  date: string; // YYYY-MM-DD
  name: string;
  isSubstitute: boolean;
  year: number;
}

export type HolidaySourceKey = 'bundled' | 'existing' | 'publicApi';
export type RequestedSource = 'auto' | HolidaySourceKey;

export interface SourceProbe { available: boolean; reason?: string }

export interface PreviewItem {
  date: string;
  name: string;
  countryCode: string;
  isSubstitute: boolean;
  year: number;
  action: 'inserted' | 'updated' | 'skipped';
}

export interface ImportResult {
  source: HolidaySourceKey;
  country: string;
  year: number;
  inserted: number;
  updated: number;
  skipped: number;
  total: number;
  items: PreviewItem[];
}

export async function listAdminHolidays(year: number, country = 'KR'): Promise<HolidayRow[]> {
  const { data } = await apiClient.get<{ holidays: HolidayRow[] }>('/admin/holidays', { params: { year, country } });
  return Array.isArray(data?.holidays) ? data.holidays : [];
}

export async function probeSources(year: number, country = 'KR'): Promise<Record<string, SourceProbe>> {
  const { data } = await apiClient.get<{ sources: Record<string, SourceProbe> }>('/admin/holidays/sources', { params: { year, country } });
  return data?.sources ?? {};
}

export async function previewImport(year: number, source: RequestedSource, country = 'KR'): Promise<ImportResult> {
  const { data } = await apiClient.post<ImportResult>('/admin/holidays/preview', { year, source, country });
  return { ...data, items: Array.isArray(data?.items) ? data.items : [] };
}

export async function applyImport(year: number, source: RequestedSource, country = 'KR'): Promise<ImportResult> {
  const { data } = await apiClient.post<ImportResult>('/admin/holidays/import', { year, source, country });
  return { ...data, items: Array.isArray(data?.items) ? data.items : [] };
}

export async function addManualHoliday(input: { date: string; name: string; isSubstitute?: boolean; country?: string }): Promise<{ action: 'inserted' | 'updated'; holiday: HolidayRow }> {
  const { data } = await apiClient.post('/admin/holidays/manual', input);
  return data;
}

export async function deleteHoliday(id: string): Promise<void> {
  await apiClient.delete(`/admin/holidays/${id}`);
}
